<template>
  <ValidationObserver ref="observer">
    <v-form @submit.prevent="submit()">
      <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
      <ValidationProvider
        v-if="whitelabel.authentication.signUp.showEmail"
        id="email"
        v-slot="{ errors }"
        name="email"
        rules="required|email"
      >
        <v-text-field
          id="landing_page_first_input"
          v-model.trim="email"
          :error-messages="errors"
          label="Email"
        />
      </ValidationProvider>
      <ValidationProvider
        v-if="whitelabel.authentication.signUp.showUniqueIdentifier"
        id="unique_identifier"
        v-slot="{ errors }"
        name="uniqueIdentifier"
        rules="required"
      >
        <v-text-field
          id="unique_identifier_input"
          v-model.trim="uniqueIdentifier"
          :error-messages="errors"
          label="Industry Number*"
        />
      </ValidationProvider>
      <ValidationProvider
        v-if="whitelabel.authentication.signUp.showPhone"
        id="phone"
        v-slot="{ errors }"
        name="phone"
        rules="required|phone"
      >
        <v-text-field
          id="phone_input"
          v-model.trim="phone"
          :error-messages="errors"
          label="Cell Number*"
        />
      </ValidationProvider>
      <ValidationProvider
        id="password"
        v-slot="{ errors }"
        name="password"
        rules="required|password"
      >
        <v-text-field
          v-model="password"
          :error-messages="errors"
          :append-icon="showPassword ? icons.mdiEye : icons.mdiEyeOff"
          :type="showPassword ? 'text' : 'password'"
          label="Password"
          @click:append="showPassword = !showPassword"
        />
      </ValidationProvider>

      <ValidationProvider
        id="confirm password"
        v-slot="{ errors }"
        name="confirm password"
        rules="required|confirmed:password"
      >
        <v-text-field
          v-model="confirmPassword"
          :error-messages="errors"
          :append-icon="showConfirmPassword ? icons.mdiEye : icons.mdiEyeOff"
          :type="showConfirmPassword ? 'text' : 'password'"
          label="Confirm Password"
          @click:append="showConfirmPassword = !showConfirmPassword"
        />
      </ValidationProvider>

      <ValidationProvider
        v-if="showWillCountries"
        id="will_country"
        v-slot="{ errors }"
        name="will_country"
        rules="required"
      >
        <v-select
          v-model="willCountry"
          :items="$options.willCountries"
          label="Which country would you like to create a will in?*"
          :error-messages="errors"
        />
      </ValidationProvider>

      <ValidationProvider
        id="accepted_terms"
        v-slot="{ errors }"
        name="accepted_terms"
        :rules="{ is: [true] }"
      >
        <v-checkbox
          v-model="acceptedTerms"
          color="primary"
          :error-messages="errors"
        >
          <template #label>
            <div class="terms-and-conditions-container">
              By signing up, I accept
              <a
                v-if="['oldmutual', 'teba'].includes(whitelabel.brand)"
                :href="
                  $router.resolve({ name: termsAndConditionsComponent }).href
                "
                target="_blank"
                @click.stop
              >
                {{ whitelabel.name }}'s terms & conditions
              </a>
              <a
                v-if="whitelabel.brand == 'quickwill'"
                target="_blank"
                :href="whitelabel.termsAndConditionsLocation"
                @click.stop
                >{{ whitelabel.name }}'s terms & conditions</a
              >
            </div>
          </template>
        </v-checkbox>
      </ValidationProvider>

      <ValidationProvider
        v-if="hasOngoingCompetition"
        id="accepted_competition_terms"
        v-slot="{ errors }"
        name="accepted_competition_terms"
      >
        <v-checkbox
          v-model="acceptedCompetitionTerms"
          color="primary"
          :error-messages="errors"
        >
          <template #label>
            <div>
              I would like to enter the competition, and accept the competition
              <a
                v-if="['oldmutual', 'teba'].includes(whitelabel.brand)"
                :href="
                  $router.resolve({ name: termsAndConditionsComponent }).href
                "
                target="_blank"
                @click.stop
              >
                terms & conditions
              </a>
              <a
                v-if="whitelabel.brand == 'quickwill'"
                target="_blank"
                :href="whitelabel.termsAndConditionsLocation"
                @click.stop
                >terms & conditions</a
              >
            </div>
          </template>
        </v-checkbox>
      </ValidationProvider>

      <ValidationProvider
        v-if="acceptedCompetitionTerms"
        id="heard_about_us"
        v-slot="{ errors }"
        name="heard_about_us"
        rules="required"
      >
        <v-select
          v-model="testatorCompetitionReferralId"
          :items="heardAboutUsOptions"
          label="Where did you hear about us?*"
          :error-messages="errors"
        />
      </ValidationProvider>

      <v-btn
        rounded
        depressed
        large
        type="submit"
        color="primary"
        class="mt-12"
        :loading="buttonLoading"
      >
        <span>Sign up</span>
        <v-icon>{{ icons.mdiArrowRight }}</v-icon>
      </v-btn>
    </v-form>
  </ValidationObserver>
</template>

<script>
  import { REGISTER_MUTATION } from '../../../constants/graphql/authentication';
  import { HOME_QUERY } from '../../../constants/graphql/home';
  import * as Sentry from '@sentry/browser';

  export default {
    name: 'SignUpForm',
    props: {
      defaultSACountry: {
        required: false,
        type: Boolean,
      },
    },
    data(props) {
      return {
        email: '',
        phone: '',
        uniqueIdentifier: '',
        password: '',
        confirmPassword: '',
        willCountry: '',
        acceptedTerms: false,
        errorMessage: '',
        showPassword: false,
        showConfirmPassword: false,
        buttonLoading: false,
        defaultSACountrySpecified: props.defaultSACountry,
        acceptedCompetitionTerms: false,
        testatorCompetitionReferralId: null,
        home: {},
      };
    },
    willCountries: [
      { text: 'South Africa', value: 'ZA' },
      { text: 'United Kingdom', value: 'GB' },
    ],
    computed: {
      showWillCountries() {
        // return (
        //   process.env.VUE_APP_INCLUDE_GB_WILL &&
        //   !this.defaultSACountrySpecified === true
        // );
        return false;
      },
      termsAndConditionsComponent() {
        if (this.home?.competition?.competitionActive) {
          return 'CompetitionTermsAndConditions';
        } else {
          return 'TermsAndConditions';
        }
      },
      hasOngoingCompetition() {
        return this.home?.competition?.competitionActive;
      },
      heardAboutUsOptions() {
        return this.home.competition.competitionReferrals.map(option => ({
          text: option.referralText,
          value: option.id,
        }));
      },
    },
    methods: {
      submit() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            this.register();
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      register() {
        const email = this.email;
        const phone = this.phone;
        const uniqueIdentifier = this.uniqueIdentifier;
        const password = this.password;
        const acceptedTerms = this.acceptedTerms;
        const referralCode =
          this.$cookies.get('referralCode') || this.$route.params.id;
        const willCountry = this.showWillCountries ? this.willCountry : 'ZA';
        const acceptedCompetitionTerms = this.acceptedCompetitionTerms;
        const testatorCompetitionReferralId =
          this.testatorCompetitionReferralId;

        const omtId = this.$store.getters.omtId;
        const adReferrer = this.$store.getters.adReferrer;
        const fullReferrer = this.$store.getters.fullReferrer;

        this.$apollo
          .mutate({
            mutation: REGISTER_MUTATION,
            variables: {
              email,
              phone,
              uniqueIdentifier,
              password,
              acceptedTerms,
              referralCode,
              willCountry,
              acceptedCompetitionTerms,
              testatorCompetitionReferralId,
              omtId,
              adReferrer,
              fullReferrer,
              signupMethod: 'Default',
              ssoToken: null,
            },
          })
          .then(result => {
            const registerTestator = result.data.registerTestator;
            const accessToken = registerTestator.accessToken;
            const refreshToken = registerTestator.refreshToken;
            const testatorId = registerTestator.testatorId;
            const userId = registerTestator.userId;
            const willCountry = registerTestator.willCountry;

            this.$cookies.remove('referralCode');

            this.$store.dispatch('login', {
              accessToken,
              refreshToken,
              testatorId,
              userId,
              willCountry,
            });
            this.setSentryUser(userId);
            this.redirectToNext();
          })
          .catch(error => {
            this.onError(error);
          });
      },
      onError(error) {
        this.buttonLoading = false;
        this.errorMessage = error.message.replace('GraphQL error:', '').trim();
      },
      setSentryUser(userId) {
        Sentry.setUser({ id: userId });
      },
      redirectToNext() {
        this.$router.push('/overview');
        this.buttonLoading = false;
      },
    },
    apollo: {
      home: {
        query: HOME_QUERY,
        fetchPolicy: 'cache-and-network',
      },
    },
  };
</script>

<style lang="scss" scoped>
  @media (max-width: 936px) {
    .terms-and-conditions-container {
      font-size: 16px;
      font-style: normal;
      font-weight: 500;
      line-height: 150%;
      padding-right: 80px;
    }
  }
</style>
